import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { Globe2, Plus } from "lucide-react";
import { ResponsiveDataList } from "../../../../../components/responsive";
import type { LifecycleRetreat } from "../../../../../types/retreatLifecycle";
import { formatParishOptionLabel } from "../../../../../utils/parishLabels";
import {
  formatLanguage,
  formatProjectedDates,
  statusChip,
  typeChip,
} from "../../lifecycle/lifecycleUi";
import { SectionShell } from "../../lifecycle/SectionShell";
import {
  projectedRetreatTitleCell,
  tooltipCell,
} from "../../lifecycle/manageRetreatColumns";
import { ProjectedRetreatCard } from "../cards/ProjectedRetreatCard";
import { SectionActionButtons } from "../SectionActionButtons";
import { projectedSupportParts, SECTION_IDS } from "../manageRetreatTypes";
import { DASHBOARD_FONT, dashboardOutlinedButtonSx } from "../dashboardUi";

const TYPE_OPTIONS = [
  "Established Parish",
  "New Parish / First Retreat",
  "Archbishop Outreach Retreat",
] as const;

export function ProjectedSection({
  rows,
  total,
  loading,
  searchValue,
  onSearchChange,
  parishes,
  projectedParishFilter,
  projectedTypeFilter,
  onParishFilter,
  onTypeFilter,
  onClearFilters,
  dataFetchApi,
  isSuperAdmin,
  onHistory,
  onDelete,
  onApprove,
  onRestore,
}: {
  rows: LifecycleRetreat[];
  total: number;
  loading: boolean;
  searchValue: string;
  onSearchChange: (v: string) => void;
  parishes: any[];
  projectedParishFilter: string;
  projectedTypeFilter: string;
  onParishFilter: (value: string) => void;
  onTypeFilter: (type: string) => void;
  onClearFilters: () => void;
  dataFetchApi: (...args: any[]) => void;
  isSuperAdmin: boolean;
  onHistory: (r: LifecycleRetreat) => void;
  onDelete: (r: LifecycleRetreat) => void;
  onApprove: (r: LifecycleRetreat) => void;
  onRestore: (r: LifecycleRetreat) => void;
}) {
  const navigate = useNavigate();

  const columns = [
    {
      id: "action",
      name: "Action",
      sortable: false,
      button: true,
      grow: 0,
      cell: (r: LifecycleRetreat) => (
        <SectionActionButtons
          r={r}
          kind="projected"
          isSuperAdmin={isSuperAdmin}
          onHistory={onHistory}
          onDelete={onDelete}
          onApprove={onApprove}
          onRestore={onRestore}
        />
      ),
      width: "200px",
    },
    {
      id: "retreatId",
      name: "Retreat Id",
      sortable: true,
      grow: 0,
      selector: (r: LifecycleRetreat) => r.id,
      cell: (r: LifecycleRetreat) => tooltipCell(String(r.id ?? "—")),
      width: "130px",
    },
    {
      id: "title",
      name: "Retreat",
      sortable: true,
      grow: 0,
      cell: projectedRetreatTitleCell,
      width: "300px",
    },
    // {
    //   id: "language",
    //   name: "Language",
    //   sortable: true,
    //   grow: 0,
    //   selector: (r: LifecycleRetreat) => formatLanguage(r.language),
    //   cell: (r: LifecycleRetreat) => tooltipCell(formatLanguage(r.language)),
    //   width: "120px",
    // },
    {
      id: "season",
      name: "Season",
      sortable: false,
      grow: 0,
      selector: (r: LifecycleRetreat) => r.seasonLabel,
      cell: (r: LifecycleRetreat) => tooltipCell(r.seasonLabel),
      width: "140px",
    },
    // {
    //   id: "dateFrom",
    //   name: "Dates",
    //   sortable: true,
    //   grow: 0,
    //   selector: (r: LifecycleRetreat) =>
    //     formatProjectedDates(r.dateFrom, r.dateTo),
    //   cell: (r: LifecycleRetreat) =>
    //     tooltipCell(formatProjectedDates(r.dateFrom, r.dateTo)),
    //   width: "120px",
    // },
    {
      id: "lead",
      name: "Lead Couple",
      sortable: false,
      grow: 0,
      selector: (r: LifecycleRetreat) => r.leadCoupleName || "Not assigned",
      cell: (r: LifecycleRetreat) =>
        tooltipCell(r.leadCoupleName || "Not assigned"),
      width: "180px",
    },
    {
      id: "support",
      name: "Support",
      sortable: false,
      grow: 0,
      selector: (r: LifecycleRetreat) => {
        const { typeLabel, couple } = projectedSupportParts(r);
        return [typeLabel, couple].filter(Boolean).join(" · ") || "Unassigned";
      },
      cell: (r: LifecycleRetreat) => {
        const { typeLabel, couple } = projectedSupportParts(r);
        const tip =
          [typeLabel, couple].filter(Boolean).join(" · ") || "Unassigned";
        if (!typeLabel && !couple) return tooltipCell("Unassigned");
        return tooltipCell(
          <Box sx={{ lineHeight: 1.25, py: 0.25, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                color: "#075fca",
                fontSize: "0.78rem",
                fontWeight: 600,
              }}
            >
              {typeLabel || "—"}
            </Typography>
            {couple ? (
              <Typography variant="body2" sx={{ fontSize: "0.78rem" }}>
                {couple}
              </Typography>
            ) : null}
          </Box>,
          tip,
        );
      },
      width: "180px",
    },
    {
      id: "retreatType",
      name: "Type",
      sortable: false,
      grow: 0,
      cell: (r: LifecycleRetreat) =>
        tooltipCell(typeChip(r.retreatType), String(r.retreatType || "—")),
      width: "190px",
    },
    {
      id: "lifecycleStatus",
      name: "Status",
      sortable: true,
      grow: 0,
      cell: (r: LifecycleRetreat) =>
        tooltipCell(
          statusChip(r.lifecycleStatus),
          String(r.lifecycleStatus || "—"),
        ),
      width: "120px",
    },
    {
      id: "readinessPercent",
      name: "Planning Readiness",
      sortable: true,
      grow: 0,
      cell: (r: LifecycleRetreat) =>
        tooltipCell(
          <Stack direction="row" spacing={1} alignItems="center">
            <LinearProgress
              variant="determinate"
              value={r.readinessPercent || 0}
              sx={{ width: 100, height: 8, borderRadius: 10 }}
            />
            <Typography variant="caption" fontWeight={700}>
              {r.readinessPercent || 0}%
            </Typography>
          </Stack>,
          `${r.readinessPercent || 0}%`,
        ),
      width: "200px",
    },
  ];

  return (
    <SectionShell
      id={SECTION_IDS.projected}
      title="Projected Retreats"
      subtitle="Executive Committee portfolio planning. These records are internal and not yet public."
      bgcolor="#eef4fa"
      icon={<Globe2 size={22} color="#4c6782" />}
      total={total}
      searchValue={searchValue}
      onSearchChange={onSearchChange}
      action={
        <Button
          startIcon={<Plus size={16} />}
          variant="outlined"
          onClick={() => navigate("/retreats/project")}
          sx={dashboardOutlinedButtonSx}
        >
          Project Retreat
        </Button>
      }
      extraToolbar={
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControl
            size="small"
            sx={{
              minWidth: 200,
              bgcolor: "white",
              fontFamily: DASHBOARD_FONT,
              "& .MuiInputBase-root, & .MuiInputLabel-root": {
                fontFamily: DASHBOARD_FONT,
              },
            }}
          >
            <InputLabel>Parish filter</InputLabel>
            <Select
              label="Parish filter"
              value={projectedParishFilter}
              onChange={(e) => onParishFilter(String(e.target.value))}
            >
              <MenuItem value="">All parishes</MenuItem>
              {parishes.map((p) => (
                <MenuItem key={p.id} value={String(p.id)}>
                  {formatParishOptionLabel(p)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            size="small"
            variant="outlined"
            onClick={onClearFilters}
            sx={dashboardOutlinedButtonSx}
          >
            View All
          </Button>
        </Stack>
      }
    >
      <Box sx={{ p: 1.5, borderBottom: "1px solid #e5e7eb" }}>
        <Stack
          direction="row"
          sx={{ gap: 1, flexWrap: "wrap", alignItems: "center" }}
        >
          <Typography
            variant="caption"
            sx={{
              fontFamily: DASHBOARD_FONT,
              fontWeight: 700,
              color: "#213449",
            }}
          >
            Retreat type legend:
          </Typography>
          {TYPE_OPTIONS.map((type) => (
            <React.Fragment key={type}>
              {typeChip(type, {
                onClick: () =>
                  onTypeFilter(projectedTypeFilter === type ? "" : type),
                isSelected: projectedTypeFilter === type,
                clickable: true,
              })}
            </React.Fragment>
          ))}
          {projectedTypeFilter && (
            <Button
              size="small"
              sx={{
                fontFamily: DASHBOARD_FONT,
                fontSize: "0.75rem",
                textTransform: "none",
                py: 0,
                px: 1,
                ml: { xs: 0, sm: 1 },
                color: "#5f7082",
                "&:hover": { color: "#213449" },
              }}
              onClick={() => onTypeFilter("")}
            >
              Clear filter
            </Button>
          )}
        </Stack>
      </Box>
      <ResponsiveDataList
        data={rows}
        total={total}
        loading={loading}
        isServerSide
        sortServer
        countPerPage={10}
        dataFetchApi={dataFetchApi}
        columns={columns}
        cardGridColumns={{ xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }}
        renderCard={(r) => (
          <ProjectedRetreatCard
            r={r}
            isSuperAdmin={isSuperAdmin}
            onHistory={onHistory}
            onDelete={onDelete}
            onApprove={onApprove}
            onRestore={onRestore}
          />
        )}
      />
    </SectionShell>
  );
}
